import React from "react";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Pause from "@mui/icons-material/Pause";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import ButtonBase from "@mui/material/ButtonBase";
import MenuPages from "./MenuPages";
import { navigate } from "gatsby";
import { Chapter } from "../types/Chapter";
import { topAppBarHeight, iconColor, appBarColor, playPauseIconColor } from "../constants/BaseConstants";
import { FormatSize } from "@mui/icons-material";
import Box from "@mui/material/Box";

const titleBlockWidth = "180px";
const titleBlockWidthNarrow = "176px";

const appBar = { backgroundColor: appBarColor } as const;

const appBarContents = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

const icon = {
  color: iconColor,
  padding: "6px",
  marginLeft: "0px",
  ["@media (min-width:350px)"]: {
    padding: "8px",
    marginLeft: "0px",
    marginRight: "0px",
  },
  ["@media (min-width:400px)"]: {
    padding: "10px",
    marginLeft: "0px",
    marginRight: "0px",
  },
  ["@media (min-width:500px)"]: {
    padding: "10px",
    marginLeft: "8px",
    marginRight: "8px",
  },
} as const;

const playPauseIcon = {
  color: playPauseIconColor,
} as const;

const titleBlock = {
  width: titleBlockWidthNarrow,
  ["@media (min-width:360px)"]: { width: titleBlockWidth },
  margin: 0,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  borderLeft: "1px solid #bbbbbb",
  borderRight: "1px solid #bbbbbb",
  height: topAppBarHeight + "px",
} as const;

const titleBlockRow1 = {
  lineHeight: "1.0",
  display: "flex",
  flexDirection: "row" as const,
} as const;

const titleBlockRow2 = { lineHeight: "1.0" } as const;

const nameEnglish = { color: "#fff", fontSize: "16px", lineHeight: "1.4" } as const;
const nameTrans = { color: "#ddd", fontSize: "16px", lineHeight: "1.4" } as const;

const number = {
  position: "absolute" as const,
  left: "10px",
  color: "#eee",
  fontSize: "14px",
  lineHeight: "1.4",
} as const;

const chevron = {
  position: "absolute" as const,
  right: "10px",
  // color: "#ff9800",
  lineHeight: "1.4",
} as const;

const seperator = {
  width: "0px",
  ["@media (min-width:375px)"]: { width: "10px" },
  ["@media (min-width:450px)"]: { width: "20px" },
} as const;

interface AppBarMainProps {
  isAudioPlaying: boolean;
  chapter: Chapter;
  onClickTitleBlock: (event: React.MouseEvent<HTMLElement>) => void;
  onClickPlayPauseButton: () => void;
  onClickSearchButton: () => void;
  onClickTextSizeButton: (event: React.MouseEvent<HTMLElement>) => void;
  bookmarkClicked?: () => void;
  gotoBookmarkClicked?: () => void;
  shareVersesClicked?: () => void;
}

function AppBarMainImpl(props: AppBarMainProps): JSX.Element {
  // Anchor drives open/close — no separate "isOpen" state needed
  const [anchorPagesMenu, setAnchorPagesMenu] = React.useState<HTMLElement | null>(null);
  const isPagesMenuOpen = Boolean(anchorPagesMenu);
  const [isSharingAvailable, setIsSharingAvailable] = React.useState(false);

  // SSR-safe Web Share API detection
  React.useEffect(() => {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      setIsSharingAvailable(true);
    }
  }, []);

  const handlePagesMenuClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorPagesMenu(event.currentTarget);
  }, []);

  const closePagesMenu = React.useCallback(() => {
    setAnchorPagesMenu(null);
  }, []);

  const handleBookmarkClicked = React.useCallback(() => {
    closePagesMenu();
    props.bookmarkClicked?.();
  }, [closePagesMenu, props]);

  const handleGotoBookmarkClicked = React.useCallback(() => {
    closePagesMenu();
    props.gotoBookmarkClicked?.();
  }, [closePagesMenu, props]);

  const handleShareVersesClicked = React.useCallback(() => {
    closePagesMenu();
    props.shareVersesClicked?.();
  }, [closePagesMenu, props]);

  const gotoNewUrl = React.useCallback(
    (url: string) => {
      closePagesMenu();
      navigate(url);
    },
    [closePagesMenu],
  );

  return (
    <Box>
      <AppBar position="fixed" sx={appBar}>
        <Box sx={appBarContents}>
          <IconButton
            size="medium"
            color="inherit"
            aria-label="Menu"
            onClick={handlePagesMenuClick}
            sx={icon}
          >
            <MenuIcon />
          </IconButton>

          <IconButton
            size="medium"
            color="inherit"
            aria-label="Text size"
            onClick={props.onClickTextSizeButton}
            sx={icon}
          >
            <FormatSize />
          </IconButton>

          <Box sx={seperator} />

          <ButtonBase onClick={props.onClickTitleBlock} title="Select Surah/Chapter">
            <Box sx={titleBlock}>
              <Box sx={titleBlockRow1}>
                <Box sx={number}>{props.chapter.chapterNumber}</Box>
                <Box sx={nameTrans}>{props.chapter.nameTrans}</Box>
                <Box sx={chevron}>▼</Box>
              </Box>
              <Box sx={titleBlockRow2}>
                <Box sx={nameEnglish}>{props.chapter.nameEnglish}</Box>
              </Box>
            </Box>
          </ButtonBase>

          <Box sx={seperator} />

          <IconButton
            size="medium"
            color="inherit"
            aria-label="Search"
            onClick={props.onClickSearchButton}
            sx={icon}
            >
            <SearchIcon />
          </IconButton>

          <IconButton
            size="medium"
            color="inherit"
            aria-label={props.isAudioPlaying ? "Pause audio" : "Play audio"}
            aria-pressed={props.isAudioPlaying}
            onClick={props.onClickPlayPauseButton}
            sx={[icon, playPauseIcon]}
          >
            {props.isAudioPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
        </Box>
      </AppBar>

      <MenuPages
        isOpen={isPagesMenuOpen}
        onClose={closePagesMenu}
        anchorEl={anchorPagesMenu}
        chapterNumber={props.chapter.chapterNumber}
        isSharingAvailable={isSharingAvailable}
        isShowBookmarkEntries={true}
        bookmarkClicked={handleBookmarkClicked}
        gotoBookmarkClicked={handleGotoBookmarkClicked}
        shareVersesClicked={handleShareVersesClicked}
        onNewUrl={gotoNewUrl}
      />
    </Box>
  );
}

// Memoize: only re-render when visible bits or handlers actually change
const AppBarMain = React.memo(AppBarMainImpl, (prev, next) => {
  if (prev.isAudioPlaying !== next.isAudioPlaying) return false;

  // Only the fields we actually render matter
  if (prev.chapter.chapterNumber !== next.chapter.chapterNumber) return false;
  if (prev.chapter.nameTrans !== next.chapter.nameTrans) return false;
  if (prev.chapter.nameEnglish !== next.chapter.nameEnglish) return false;

  // Handlers should be stable (parent uses useCallback); if they change, re-render
  if (prev.onClickTitleBlock !== next.onClickTitleBlock) return false;
  if (prev.onClickPlayPauseButton !== next.onClickPlayPauseButton) return false;
  if (prev.onClickSearchButton !== next.onClickSearchButton) return false;
  if (prev.onClickTextSizeButton !== next.onClickTextSizeButton) return false;

  // Optional handlers
  if (prev.bookmarkClicked !== next.bookmarkClicked) return false;
  if (prev.gotoBookmarkClicked !== next.gotoBookmarkClicked) return false;
  if (prev.shareVersesClicked !== next.shareVersesClicked) return false;

  return true; // skip re-render
});

export default AppBarMain;
